import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, ChefHat, Trash2, DollarSign, TrendingUp, Edit, Ban, CheckCircle } from 'lucide-react';
import axios from 'axios';

import { useAuth, API, getImageUrl } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../ui/dialog';
import { getErrorMessage } from '../../utils/errorHandler';
import { formatAmount } from '../../utils/format';
const RecipesTab = () => {
  const { getAuthHeaders } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [products, setProducts] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [formData, setFormData] = useState({
    product_id: '',
    ingredients: [],
    instructions: ['']
  });
  const [selectedIngredient, setSelectedIngredient] = useState('');
  const [ingredientQuantity, setIngredientQuantity] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [recipesRes, productsRes, ingredientsRes] = await Promise.all([
        axios.get(`${API}/recipes`, { headers: getAuthHeaders() }),
        axios.get(`${API}/products`, { headers: getAuthHeaders() }),
        axios.get(`${API}/ingredients`, { headers: getAuthHeaders() })
      ]);
      setRecipes(Array.isArray(recipesRes.data) ? recipesRes.data : []);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setIngredients(Array.isArray(ingredientsRes.data) ? ingredientsRes.data : []);
    } catch {
      toast.error('Error al cargar datos');
    }
  };

  const addIngredientToRecipe = () => {
    if (!selectedIngredient || ingredientQuantity <= 0) {
      toast.error('Selecciona un ingrediente y cantidad válida');
      return;
    }

    const ingredient = ingredients.find(i => i.id === selectedIngredient);
    if (!ingredient) return;

    const newIngredient = {
      ingredient_id: ingredient.id,
      ingredient_name: ingredient.name,
      quantity: ingredientQuantity,
      unit: ingredient.unit
    };

    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, newIngredient]
    });

    setSelectedIngredient('');
    setIngredientQuantity(0);
  };

  const removeIngredient = (index) => {
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter((_, i) => i !== index)
    });
  };

  const addInstruction = () => {
    setFormData({
      ...formData,
      instructions: [...formData.instructions, '']
    });
  };

  const updateInstruction = (index, value) => {
    const newInstructions = [...formData.instructions];
    newInstructions[index] = value;
    setFormData({
      ...formData,
      instructions: newInstructions
    });
  };

  const removeInstruction = (index) => {
    setFormData({
      ...formData,
      instructions: formData.instructions.filter((_, i) => i !== index)
    });
  };

  const handleCreateRecipe = async (e) => {
    e.preventDefault();
    
    if (formData.ingredients.length === 0) {
      toast.error('Agrega al menos un ingrediente');
      return;
    }

    setLoading(true);

    try {
      if (editingRecipe) {
        // Update existing recipe
        await axios.put(`${API}/recipes/${editingRecipe.id}`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Receta actualizada exitosamente');
      } else {
        // Create new recipe
        await axios.post(`${API}/recipes`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Receta creada exitosamente');
      }
      
      await loadData();
      handleCloseDialog();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar receta'));
    }

    setLoading(false);
  };

  const handleEditRecipe = (recipe) => {
    setEditingRecipe(recipe);
    setFormData({
      product_id: recipe.product_id,
      ingredients: recipe.ingredients,
      instructions: recipe.instructions || ['']
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setTimeout(() => {
      setEditingRecipe(null);
      setFormData({ product_id: '', ingredients: [], instructions: [''] });
      setSelectedIngredient('');
      setIngredientQuantity(0);
    }, 100);
  };

  const handleOpenDialog = () => {
    setEditingRecipe(null);
    setFormData({ product_id: '', ingredients: [], instructions: [''] });
    setSelectedIngredient('');
    setIngredientQuantity(0);
    setIsDialogOpen(true);
  };

  const handleDeleteRecipe = async (recipeId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta receta?')) {
      return;
    }

    try {
      await axios.delete(`${API}/recipes/${recipeId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Receta eliminada exitosamente');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar receta'));
    }
  };

  const handleToggleActive = async (recipe) => {
    try {
      await axios.patch(
        `${API}/recipes/${recipe.id}/toggle-active`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Receta ${recipe.is_active !== false ? 'deshabilitada' : 'habilitada'}`);
      loadData();
    } catch {
      toast.error('Error al cambiar estado de la receta');
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.name || 'Producto no encontrado';
  };


  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Recetas</h2>
          <p className="text-sm text-gray-600">Vincula productos con ingredientes para control de costos</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={handleOpenDialog}
              data-testid="create-recipe-button" 
              className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Receta
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRecipe ? 'Editar Receta' : 'Crear Nueva Receta'}</DialogTitle>
              <DialogDescription>
                {editingRecipe
                  ? 'Modifica los ingredientes e instrucciones de la receta'
                  : 'Crea una receta vinculando productos con ingredientes para control de costos'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateRecipe} className="space-y-4">
              <div>
                <Label>Producto</Label>
                <Select
                  value={formData.product_id}
                  onValueChange={(value) => setFormData({ ...formData, product_id: value })}
                  required
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Selecciona producto" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((prod) => (
                      <SelectItem key={prod.id} value={prod.id}>
                        {prod.name} - ${formatAmount(prod.price)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Add Ingredients */}
              <div className="border rounded-xl p-4 bg-gray-50">
                <Label className="mb-3 block">Ingredientes de la Receta</Label>
                
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <Select
                    value={selectedIngredient}
                    onValueChange={setSelectedIngredient}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Ingrediente" />
                    </SelectTrigger>
                    <SelectContent>
                      {ingredients.map((ing) => (
                        <SelectItem key={ing.id} value={ing.id}>
                          {ing.name} ({ing.unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Cantidad"
                    value={ingredientQuantity}
                    onChange={(e) => setIngredientQuantity(parseFloat(e.target.value) || 0)}
                    className="rounded-xl"
                  />
                  
                  <Button type="button" onClick={addIngredientToRecipe} variant="outline" className="rounded-xl">
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar
                  </Button>
                </div>

                {/* Ingredients List */}
                {formData.ingredients.length > 0 && (
                  <div className="space-y-2">
                    {formData.ingredients.map((ing, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-white rounded-lg">
                        <span className="text-sm text-gray-800">
                          <strong>{ing.ingredient_name}</strong>: {ing.quantity} {ing.unit}
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeIngredient(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div>
                <Label className="mb-2 block">Instrucciones de Preparación</Label>
                <div className="space-y-2">
                  {formData.instructions.map((instruction, index) => (
                    <div key={index} className="flex gap-2">
                      <span className="text-sm font-semibold text-gray-600 mt-2">{index + 1}.</span>
                      <Textarea
                        value={instruction}
                        onChange={(e) => updateInstruction(index, e.target.value)}
                        placeholder="Describe el paso..."
                        className="rounded-xl flex-1"
                      />
                      {formData.instructions.length > 1 && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeInstruction(index)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  onClick={addInstruction}
                  variant="outline"
                  size="sm"
                  className="mt-2 rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Paso
                </Button>
              </div>

              <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
                {loading ? 'Guardando...' : (editingRecipe ? 'Actualizar Receta' : 'Crear Receta')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Recipes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {recipes.map((recipe) => {
          const profitMarginPercent = recipe.profit_margin || 0;
          const product = products.find(p => p.id === recipe.product_id);
          
          return (
            <Card
              key={recipe.id}
              data-testid={`recipe-card-${recipe.id}`}
              className={`overflow-hidden bg-white/80 backdrop-blur-xl border-orange-100 hover-lift ${
                recipe.is_active === false ? 'opacity-60 border-gray-300' : ''
              }`}
            >
              {/* Product Image */}
              {product?.image_url && (
                <div className="h-48 w-full overflow-hidden bg-gradient-to-br from-orange-100 to-red-100">
                  <img 
                    src={getImageUrl(product.image_url)} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              {!product?.image_url && (
                <div className="h-48 w-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                  <ChefHat className="w-16 h-16 text-white" />
                </div>
              )}
              
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-1">
                      {getProductName(recipe.product_id)}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {recipe.ingredients.length} ingredientes
                    </p>
                    <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full ${
                      recipe.is_active !== false
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {recipe.is_active !== false ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                </div>

              {/* Cost Analysis */}
              <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-orange-50 rounded-xl">
                <div>
                  <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                    <DollarSign className="w-3 h-3" />
                    Costo
                  </div>
                  <p className="text-lg font-bold text-gray-800">
                    ${formatAmount(recipe.total_cost)}
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                    <TrendingUp className="w-3 h-3" />
                    Margen
                  </div>
                  <p className={`text-lg font-bold ${profitMarginPercent > 60 ? 'text-green-600' : profitMarginPercent > 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {profitMarginPercent.toFixed(1)}%
                  </p>
                </div>
              </div>

              {/* Ingredients */}
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">Ingredientes:</p>
                <div className="space-y-1">
                  {recipe.ingredients.map((ing, idx) => (
                    <div key={idx} className="text-sm text-gray-600 flex justify-between">
                      <span>• {ing.ingredient_name}</span>
                      <span className="font-medium">{ing.quantity} {ing.unit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Instructions */}
              {recipe.instructions && recipe.instructions.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Preparación:</p>
                  <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                    {recipe.instructions.map((instruction, idx) => (
                      <li key={idx}>{instruction}</li>
                    ))}
                  </ol>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={() => handleToggleActive(recipe)}
                  variant="outline"
                  size="sm"
                  className={`flex-1 rounded-xl ${
                    recipe.is_active !== false
                      ? 'border-yellow-300 text-yellow-700 hover:bg-yellow-50'
                      : 'border-green-300 text-green-700 hover:bg-green-50'
                  }`}
                >
                  {recipe.is_active !== false ? (
                    <><Ban className="w-4 h-4 mr-2" /> Deshabilitar</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-2" /> Habilitar</>
                  )}
                </Button>
                <Button
                  onClick={() => handleEditRecipe(recipe)}
                  variant="outline"
                  size="sm"
                  data-testid={`edit-recipe-${recipe.id}`}
                  className="flex-1 border-orange-200 hover:bg-orange-50 rounded-xl"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
                <Button
                  onClick={() => handleDeleteRecipe(recipe.id)}
                  variant="outline"
                  size="sm"
                  data-testid={`delete-recipe-${recipe.id}`}
                  className="flex-1 border-red-200 hover:bg-red-50 text-red-600 rounded-xl"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </Button>
              </div>
              </div>
            </Card>
          );
        })}
      </div>

      {recipes.length === 0 && (
        <div className="text-center py-12">
          <ChefHat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No hay recetas creadas</p>
          <p className="text-sm text-gray-600 mb-4">
            Las recetas vinculan productos con ingredientes para control de costos y inventario
          </p>
          <Button onClick={() => setIsDialogOpen(true)} className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
            <Plus className="w-4 h-4 mr-2" />
            Crear Primera Receta
          </Button>
        </div>
      )}
    </div>
  );
};

export default RecipesTab;
